
// eslint-disable-next-line object-curly-newline
import { computed, defineComponent, reactive, h, ref, watch, WritableComputedRef } from 'vue';
import Icone from '@/core/components/Icone.vue';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { IEngenhariaMateriaPrima, IProdutoIntermediario } from '@/models/Entidades/Cadastros/PCP/IEngenhariaProduto';
import SelecionarProduto from '@/components/Cadastros/Produtos/SelecionarProduto.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import SelecionarUnidadeProdutoMovimento from '@/components/Cadastros/Produtos/Unidades/SelecionarUnidadeProdutoMovimento.vue';
import ServicoEngenhariaProduto from '@/servicos/Cadastros/PCP/ServicoEngenhariaProduto';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import UtilitarioMatematico from '@/core/utilitarios/UtilitarioMatematico';
import SelecionarEngenhariaProducao from '@/components/Cadastros/PCP/SelecionarEngenhariaProducao.vue';

export default defineComponent({
  name: 'LancarMateriaPrimaEtapaProducao',
  props: {
    visivel: {
      type: Boolean,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    materiaPrima: {
      type: Array as () => IEngenhariaMateriaPrima[],
      required: true,
    },
    codigoEtapa: {
      type: Number,
      required: true,
    },
  },
  components: {
    Icone,
    SelecionarProduto,
    SelecionarUnidadeProdutoMovimento,
    CampoNumerico,
    SelecionarEngenhariaProducao,
  },
  emits: ['salvar', 'update:visivel', 'update:materiaPrima'],
  setup(props, { emit }) {
    const {
      apresentarMensagemAlerta,
    } = useTelaBase();
    const {
      gradeBase,
    } = useGradeBase();

    const colunasMateriasPrimasPI: IColumn[] = ([
      {
        title: 'Código', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
      },
      {
        title: 'Ordem', dataIndex: 'ordem', key: 'ordem', position: 1, visible: true, width: 50,
      },
      {
        title: 'Produto', dataIndex: 'descricao', key: 'descricao', position: 2, visible: true, width: 250,
      },
      {
        title: 'Unidade', dataIndex: 'siglaUnidade', key: 'siglaUnidade', position: 3, visible: true, width: 75, align: 'center',
      },
      {
        title: 'Quantidade', dataIndex: 'quantidade', key: 'quantidade', position: 4, visible: true, width: 75, align: 'right',
      },
      {
        title: 'Custo', dataIndex: 'custoUnitario', key: 'custo', position: 5, visible: true, width: 100, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado,
      },
      {
        title: 'Total', dataIndex: 'custoTotal', key: 'total', position: 6, visible: true, width: 100, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado,
      },
    ]);
    const servicoEngenhariaProduto = new ServicoEngenhariaProduto();
    const state = reactive({
      editavel: true,
      etapaSelecionada: 0,
      indexSelecionado: -1,
      descricaoSelecionado: '',
      codigoProduto: 0,
      codigoUnidade: 0,
      quantidade: 0,
      casasDecimais: 0,
      sigla: '',
      ordem: 0,
      transferirLote: false,
      codigoEngenhariaPI: 0,
      nomeEngenhariaPI: '',
      tipoProducaoPI: 0,
      habilitarPI: false,
      produtoIntemediario: false,
    });
    const expandedKeys = ref<number[]>([]);
    const computedMateriaPrima: WritableComputedRef<IEngenhariaMateriaPrima[]> = computed({
      get: () => props.materiaPrima,
      set: (val: IEngenhariaMateriaPrima[]) => {
        emit('update:materiaPrima', val);
      },
    });

    function objetoInicial() {
      state.editavel = true;
      state.indexSelecionado = -1;
      state.codigoProduto = 0;
      state.codigoUnidade = 0;
      state.quantidade = 0;
      state.descricaoSelecionado = '';
      state.sigla = '';
      state.casasDecimais = 0;
      state.ordem = 0;
      state.transferirLote = false;
      state.habilitarPI = false;
      state.codigoEngenhariaPI = 0;
      state.nomeEngenhariaPI = '';
      state.tipoProducaoPI = 0;
      state.produtoIntemediario = false;
    }

    function limparCampos() {
      state.editavel = true;
      state.codigoUnidade = 0;
      state.indexSelecionado = -1;
      state.codigoProduto = 0;
      state.quantidade = 0;
      state.descricaoSelecionado = '';
      state.sigla = '';
      state.casasDecimais = 0;
      state.ordem = 0;
      state.transferirLote = false;
      state.produtoIntemediario = false;
      state.codigoEngenhariaPI = 0;
      state.nomeEngenhariaPI = '';
      state.tipoProducaoPI = 0;
      state.habilitarPI = false;
    }

    function preencherColunas() {
      gradeBase.colunas = [
        {
          title: 'Código', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
        },
        {
          title: 'Ordem', dataIndex: 'ordem', key: 'ordem', position: 1, visible: true, width: 50,
        },
        {
          title: 'Produto', dataIndex: 'descricao', key: 'descricao', position: 2, visible: true, width: 300,
        },
        {
          title: 'Unidade', dataIndex: 'siglaUnidade', key: 'siglaUnidade', position: 3, visible: true, width: 75, align: 'center',
        },
        {
          title: 'Quantidade', dataIndex: 'quantidade', key: 'quantidade', position: 4, visible: true, width: 100, align: 'right',
        },
        {
          title: 'Custo', dataIndex: 'custoUnitario', key: 'custo', position: 5, visible: true, width: 100, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado,
        },
        {
          title: 'Total', dataIndex: 'custoTotal', key: 'total', position: 6, visible: true, width: 100, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado,
        },
        {
          title: 'Transf. Lote', dataIndex: 'transferirLote', key: 'transferirlote', position: 7, visible: true, width: 50,
        },
        {
          title: 'Açoes', key: 'acoes', position: 8, visible: true, fixed: 'right', width: 70, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    function validaCampos() {
      if (!UtilitarioGeral.validaCodigo(state.codigoProduto)) {
        apresentarMensagemAlerta('Por favor selecione um produto!');
        return false;
      }
      if (!UtilitarioGeral.validaCodigo(state.codigoUnidade)) {
        apresentarMensagemAlerta('Por favor selecione uma unidade!');
        return false;
      }
      if (!UtilitarioGeral.validaNumerico(state.quantidade)) {
        apresentarMensagemAlerta('Por favor informe uma quantidade!');
        return false;
      }
      if (state.indexSelecionado === -1 && computedMateriaPrima.value.some((p) => p.codigoProduto === state.codigoProduto)) {
        apresentarMensagemAlerta('O produto já foi adicionado!');
        return false;
      }
      return true;
    }

    async function atualizarItens(index: number) {
      computedMateriaPrima.value[index].codigoEtapa = state.etapaSelecionada;
      computedMateriaPrima.value[index].codigoProduto = state.codigoProduto;
      computedMateriaPrima.value[index].codigoUnidade = state.codigoUnidade;
      computedMateriaPrima.value[index].quantidade = state.quantidade;
      computedMateriaPrima.value[index].descricao = state.descricaoSelecionado;
      computedMateriaPrima.value[index].siglaUnidade = state.sigla;
      computedMateriaPrima.value[index].custoUnitario = await servicoEngenhariaProduto.obterCustoProduto(state.codigoProduto);
      computedMateriaPrima.value[index].custoTotal = computedMateriaPrima.value[index].custoUnitario * computedMateriaPrima.value[index].quantidade;
      computedMateriaPrima.value[index].transferirLote = state.transferirLote;
      computedMateriaPrima.value[index].casasDecimais = state.casasDecimais;
      computedMateriaPrima.value[index].ordem = state.ordem;
      computedMateriaPrima.value[index].produtoIntermediario = state.produtoIntemediario;
      if (state.produtoIntemediario) {
        const prodIntermediario: IProdutoIntermediario = {} as IProdutoIntermediario;
        prodIntermediario.codigo = 0;
        prodIntermediario.codigoEngenhariaPI = state.codigoEngenhariaPI;
        prodIntermediario.codigoMateriaPrimaEngenharia = 0;
        computedMateriaPrima.value[index].produtoIntermediarioVinculado = prodIntermediario;
        computedMateriaPrima.value[index].materiasPrimasPI = await servicoEngenhariaProduto.obterMPsProdutoIntermediario(state.codigoEngenhariaPI);
      } else {
        computedMateriaPrima.value[index].materiasPrimasPI = [];
      }
      limparCampos();
    }

    async function adicionar(index: number) {
      if (!validaCampos()) { return; }
      if (index > -1) {
        atualizarItens(index);
        return;
      }
      const materiaPrima: IEngenhariaMateriaPrima = {} as IEngenhariaMateriaPrima;
      materiaPrima.codigoEtapa = state.etapaSelecionada;
      materiaPrima.codigoProduto = state.codigoProduto;
      materiaPrima.codigoUnidade = state.codigoUnidade;
      materiaPrima.quantidade = state.quantidade;
      materiaPrima.descricao = state.descricaoSelecionado;
      materiaPrima.siglaUnidade = state.sigla;
      materiaPrima.custoUnitario = await servicoEngenhariaProduto.obterCustoProduto(state.codigoProduto);
      const custoTotal = materiaPrima.custoUnitario * materiaPrima.quantidade;
      materiaPrima.custoTotal = UtilitarioMatematico.valorDecimal2Casas(custoTotal);
      materiaPrima.ordem = computedMateriaPrima.value.length + 1;
      materiaPrima.casasDecimais = state.casasDecimais;
      materiaPrima.transferirLote = state.transferirLote;
      if (state.produtoIntemediario) {
        const prodIntermediario: IProdutoIntermediario = {} as IProdutoIntermediario;
        prodIntermediario.codigo = 0;
        prodIntermediario.codigoEngenhariaPI = state.codigoEngenhariaPI;
        prodIntermediario.codigoMateriaPrimaEngenharia = 0;
        materiaPrima.produtoIntermediarioVinculado = prodIntermediario;
        materiaPrima.produtoIntermediario = true;
        materiaPrima.materiasPrimasPI = await servicoEngenhariaProduto.obterMPsProdutoIntermediario(state.codigoEngenhariaPI);
      }

      computedMateriaPrima.value.push(materiaPrima);
      limparCampos();
    }

    function excluir(index: number) {
      computedMateriaPrima.value.splice(index, 1);
      state.indexSelecionado = -1;
    }

    function editar(index: number) {
      state.indexSelecionado = index;
      state.codigoProduto = computedMateriaPrima.value[index].codigoProduto;
      state.quantidade = computedMateriaPrima.value[index].quantidade;
      state.descricaoSelecionado = computedMateriaPrima.value[index].descricao;
      state.casasDecimais = computedMateriaPrima.value[index].casasDecimais;
      state.sigla = computedMateriaPrima.value[index].siglaUnidade;
      state.codigoUnidade = computedMateriaPrima.value[index].codigoUnidade;
      state.transferirLote = computedMateriaPrima.value[index].transferirLote;
      state.ordem = computedMateriaPrima.value[index].ordem;
      if (computedMateriaPrima.value[index].produtoIntermediario) {
        state.produtoIntemediario = true;
        state.habilitarPI = true;
        state.codigoEngenhariaPI = computedMateriaPrima.value[index].produtoIntermediarioVinculado?.codigoEngenhariaPI || 0;
      } else {
        state.produtoIntemediario = false;
        state.codigoEngenhariaPI = 0;
        state.nomeEngenhariaPI = '';
      }
      state.editavel = true;
    }

    preencherColunas();

    async function validarProdutoIntermediario() {
      const codigoEngPI = await servicoEngenhariaProduto.obterProdutoIntermediarioVinculado(state.codigoProduto);
      if (codigoEngPI && codigoEngPI > 0) {
        state.habilitarPI = true;
      }
    }

    function expandedRowRender(record: any) {
      return h('a-table', {
        columns: colunasMateriasPrimasPI,
        dataSource: record.materiasPrimasPI,
        rowKey: 'codigo',
        pagination: false,
        size: 'small',
        style: 'margin-left: 40px; margin-bottom: 0;',
      });
    }
    watch(computedMateriaPrima, (newVal) => {
      const keysToExpand = newVal
        .filter((record) => record.materiasPrimasPI && record.materiasPrimasPI.length > 0)
        .map((record) => record.codigo);
      expandedKeys.value = keysToExpand;
    }, { immediate: true, deep: true });

    watch(() => state.transferirLote, (newValue) => {
      if (newValue) {
        state.produtoIntemediario = false;
      }
    });

    watch(() => state.produtoIntemediario, (newValue) => {
      if (newValue) {
        state.transferirLote = false;
      }
    });

    return {
      props,
      state,
      objetoInicial,
      EPermissaoDados,
      gradeBase,
      ECustomRenderRow,
      editar,
      excluir,
      adicionar,
      computedMateriaPrima,
      UtilitarioMascara,
      expandedRowRender,
      colunasMateriasPrimasPI,
      expandedKeys,
      validarProdutoIntermediario,
    };
  },
});
